/**
 * Inline marker for post-market / recurring obligations. Surfaces the
 * `triggerOrFrequency` field and exposes a tooltip with the legal basis
 * and the list of records the OEM must keep. Per Kaiyang Lesson §25.11,
 * this badge is mandatory for cards whose obligationTiming is
 * post_market / recurring / event_triggered / phase_in — the W1 schema
 * enforces the pairing.
 */

import type { ComplianceRequirementCard } from "@regpulse/contracts";

type ContinuingObligation = NonNullable<
  ComplianceRequirementCard["continuingObligation"]
>;

export function ContinuingObligationBadge({
  obligation,
}: {
  obligation: ContinuingObligation;
}) {
  const tooltip = [
    obligation.legalBasis,
    obligation.reportName ? `report: ${obligation.reportName}` : undefined,
    obligation.requiredRecords.length > 0
      ? `records: ${obligation.requiredRecords.join(", ")}`
      : undefined,
    obligation.nextDueWindow ? `next due: ${obligation.nextDueWindow}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  // Deep Purple per "4+2 Lifecycle Architecture" diagram §
  // Continuing Obligations. See tokens.css §1.3.
  return (
    <span
      title={tooltip}
      data-chip="continuing-obligation-detail"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-4)",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        border: "1px solid var(--ft-continuing)",
        borderRadius: "var(--radius-full)",
        padding: "1px var(--space-8)",
        background: "var(--ft-continuing-soft)",
        color: "var(--ft-continuing)",
      }}
    >
      <span aria-hidden>⟳</span>
      <span>continuing · {obligation.triggerOrFrequency}</span>
    </span>
  );
}
